<script setup lang="ts">
import type { TableColumn } from '@nuxt/ui'

import type {
  CreateLanguageTestSyncJobRequest,
  LanguageTestSyncJobDTO,
  UpdateLanguageTestSyncJobRequest,
} from '@/types/admin'
import type { Language } from '@/types/languages'

import { toAppColor } from '@/composables/useAppColor'
import { appConfirm } from '@/composables/useAppConfirm'
import { useAppToast } from '@/composables/useAppToast'
import { useMetadata } from '@/composables/useMetadata'
import { adminService } from '@/services/adminService'
import { OrderedLanguages } from '@/types/languages'

definePage({
  meta: {
    requiresAuth: true,
    requiresAdmin: true,
  },
})

const { t, d } = useI18n()
const { setPageTitle } = useBreadcrumbs()
const toast = useAppToast()
const { availableSemesters, selectedSemester } = useMetadata()

const jobs = ref<LanguageTestSyncJobDTO[]>([])
const isLoading = ref(false)
const isSaving = ref(false)
const error = ref<string | null>(null)
const dialogVisible = ref(false)
const runningJobId = ref<number | null>(null)
const selectedJob = ref<LanguageTestSyncJobDTO | null>(null)
const columns = computed<TableColumn<LanguageTestSyncJobDTO>[]>(() => [
  { accessorKey: 'name', header: t('pages.admin.syncJobs.columns.name') },
  { accessorKey: 'language', header: t('pages.admin.syncJobs.columns.language') },
  { accessorKey: 'iliasCourseId', header: t('pages.admin.syncJobs.columns.iliasCourseId') },
  { accessorKey: 'semesterName', header: t('pages.admin.syncJobs.columns.semester') },
  { accessorKey: 'isActive', header: t('pages.admin.syncJobs.columns.isActive') },
  { accessorKey: 'lastSyncAt', header: t('pages.admin.syncJobs.columns.lastSyncAt') },
  {
    accessorKey: 'lastSyncResultCount',
    header: t('pages.admin.syncJobs.columns.lastSyncResultCount'),
  },
  { accessorKey: 'testResultCount', header: t('pages.admin.syncJobs.columns.testResultCount') },
  {
    accessorKey: 'assignedWorkspaceCount',
    header: t('pages.admin.syncJobs.columns.assignedWorkspaceCount'),
  },
  { accessorKey: 'lastSyncError', header: t('pages.admin.syncJobs.columns.lastSyncError') },
  { id: 'actions', header: t('pages.admin.syncJobs.columns.actions') },
])

const formData = reactive({
  name: '',
  language: OrderedLanguages[0] as Language,
  iliasCourseId: '',
  isActive: true,
  semesterId: null as number | null,
})

const formErrors = reactive({
  name: '',
  language: '',
  iliasCourseId: '',
  semesterId: '',
})

const languageOptions = computed(() =>
  OrderedLanguages.map((lang) => ({
    label: t(`enums.spokenLanguage.${lang}`),
    value: lang,
  })),
)

const semesterOptions = computed(() =>
  availableSemesters.value.map((sem) => ({
    label: sem.nameDE,
    value: sem.id,
  })),
)

function resetForm() {
  formData.name = ''
  formData.language = OrderedLanguages[0]
  formData.iliasCourseId = ''
  formData.isActive = true
  formData.semesterId = selectedSemester.value?.id ?? null
  formErrors.name = ''
  formErrors.language = ''
  formErrors.iliasCourseId = ''
  formErrors.semesterId = ''
}

function openCreateDialog() {
  selectedJob.value = null
  resetForm()
  dialogVisible.value = true
}

function openEditDialog(job: LanguageTestSyncJobDTO) {
  selectedJob.value = job
  formData.name = job.name
  formData.language = job.language
  formData.iliasCourseId = job.iliasCourseId
  formData.isActive = job.isActive
  formData.semesterId = job.semesterId
  formErrors.name = ''
  formErrors.language = ''
  formErrors.iliasCourseId = ''
  formErrors.semesterId = ''
  dialogVisible.value = true
}

function validateForm(): boolean {
  let isValid = true
  formErrors.name = ''
  formErrors.language = ''
  formErrors.iliasCourseId = ''
  formErrors.semesterId = ''

  if (!formData.name.trim()) {
    formErrors.name = t('pages.admin.syncJobs.validation.nameRequired')
    isValid = false
  }

  if (!formData.language) {
    formErrors.language = t('pages.admin.syncJobs.validation.languageRequired')
    isValid = false
  }

  if (!formData.iliasCourseId.trim()) {
    formErrors.iliasCourseId = t('pages.admin.syncJobs.validation.courseIdRequired')
    isValid = false
  }

  if (!formData.semesterId) {
    formErrors.semesterId = t('pages.admin.syncJobs.validation.semesterRequired')
    isValid = false
  }

  return isValid
}

async function loadJobs() {
  isLoading.value = true
  error.value = null
  try {
    jobs.value = await adminService.getLanguageTestSyncJobs()
  } catch (err) {
    console.error('Error loading language test sync jobs:', err)
    error.value = t('pages.admin.syncJobs.messages.loadError')
  } finally {
    isLoading.value = false
  }
}

async function saveJob() {
  if (!validateForm()) return

  isSaving.value = true
  try {
    if (selectedJob.value) {
      const request: UpdateLanguageTestSyncJobRequest = {
        name: formData.name.trim(),
        language: formData.language,
        iliasCourseId: formData.iliasCourseId.trim(),
        semesterId: formData.semesterId!,
        isActive: formData.isActive,
      }
      await adminService.updateLanguageTestSyncJob(selectedJob.value.id, request)
      toast.add({
        color: 'success',
        title: t('common.messages.success'),
        description: t('pages.admin.syncJobs.messages.updateSuccess'),
        duration: 3000,
      })
    } else {
      const request: CreateLanguageTestSyncJobRequest = {
        name: formData.name.trim(),
        language: formData.language,
        iliasCourseId: formData.iliasCourseId.trim(),
        semesterId: formData.semesterId!,
        isActive: formData.isActive,
      }
      await adminService.createLanguageTestSyncJob(request)
      toast.add({
        color: 'success',
        title: t('common.messages.success'),
        description: t('pages.admin.syncJobs.messages.createSuccess'),
        duration: 3000,
      })
    }

    dialogVisible.value = false
    await loadJobs()
  } catch (err) {
    console.error('Error saving language test sync job:', err)
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: t('pages.admin.syncJobs.messages.saveError'),
      duration: 4000,
    })
  } finally {
    isSaving.value = false
  }
}

async function confirmDelete(job: LanguageTestSyncJobDTO) {
  const accepted = await appConfirm({
    message: t('pages.admin.syncJobs.dialog.confirmDeleteMessage', { name: job.name }),
    title: t('pages.admin.syncJobs.dialog.confirmDeleteTitle'),
    confirmLabel: t('pages.admin.syncJobs.actions.delete'),
    cancelLabel: t('common.actions.cancel'),
    confirmColor: 'error',
  })
  if (accepted) await handleDelete(job)
}

async function handleDelete(job: LanguageTestSyncJobDTO) {
  try {
    await adminService.deleteLanguageTestSyncJob(job.id)
    toast.add({
      color: 'success',
      title: t('common.messages.success'),
      description: t('pages.admin.syncJobs.messages.deleteSuccess'),
      duration: 3000,
    })
    await loadJobs()
  } catch (err) {
    console.error('Error deleting language test sync job:', err)
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: t('pages.admin.syncJobs.messages.deleteError'),
      duration: 4000,
    })
  }
}

async function runJob(job: LanguageTestSyncJobDTO) {
  runningJobId.value = job.id
  try {
    const response = await adminService.runLanguageTestSyncJob(job.id)
    toast.add({
      color: 'success',
      title: t('common.messages.success'),
      description: response.message || t('pages.admin.syncJobs.messages.runQueued'),
      duration: 3000,
    })
  } catch (err) {
    console.error('Error running language test sync job:', err)
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: t('pages.admin.syncJobs.messages.runError'),
      duration: 4000,
    })
  } finally {
    runningJobId.value = null
  }
}

onMounted(() => {
  setPageTitle(t('pages.admin.syncJobs.title'))
  loadJobs()
})
</script>

<template>
  <div class="space-y-6">
    <div class="flex flex-wrap items-center justify-between gap-4">
      <div>
        <h1 class="text-2xl font-bold">{{ t('pages.admin.syncJobs.title') }}</h1>
        <p class="text-muted">
          {{ t('pages.admin.syncJobs.subtitle') }}
        </p>
      </div>
      <div class="flex flex-wrap gap-2">
        <UButton
          icon="i-lucide-refresh-cw"
          :label="t('pages.admin.syncJobs.actions.refresh')"
          :loading="isLoading"
          @click="loadJobs"
          variant="outline"
        />
        <UButton
          icon="i-lucide-plus"
          :label="t('pages.admin.syncJobs.actions.create')"
          @click="openCreateDialog"
        />
      </div>
    </div>

    <AppAlert v-if="error" color="error" :closable="false">
      {{ error }}
    </AppAlert>

    <AppDataTableShell :data="jobs" :columns="columns" :loading="isLoading">
      <template #empty>
        <div class="py-10 text-center">
          <UIcon name="i-lucide-database" class="mb-3 text-4xl text-dimmed" />
          <p class="text-lg font-medium">{{ t('pages.admin.syncJobs.empty.title') }}</p>
          <p class="text-muted">
            {{ t('pages.admin.syncJobs.empty.subtitle') }}
          </p>
        </div>
      </template>

      <template #language-cell="{ row }">
        {{ t(`enums.spokenLanguage.${row.original.language}`) }}
      </template>
      <template #semesterName-cell="{ row }">{{ row.original.semesterName ?? '-' }}</template>
      <template #isActive-cell="{ row }">
        <UBadge
          :label="row.original.isActive ? t('common.status.active') : t('common.status.inactive')"
          :color="toAppColor(row.original.isActive ? 'success' : 'danger')"
        />
      </template>
      <template #lastSyncAt-cell="{ row }">
        {{ row.original.lastSyncAt ? d(new Date(row.original.lastSyncAt), 'datetime') : '-' }}
      </template>
      <template #lastSyncResultCount-cell="{ row }">
        {{ row.original.lastSyncResultCount ?? '-' }}
      </template>
      <template #lastSyncError-cell="{ row }">
        <span class="text-muted">{{ row.original.lastSyncError ?? '-' }}</span>
      </template>
      <template #actions-cell="{ row }">
        <div class="flex gap-2">
          <UButton
            icon="i-lucide-play"
            color="neutral"
            :aria-label="t('pages.admin.syncJobs.actions.run')"
            :loading="runningJobId === row.original.id"
            @click="runJob(row.original)"
            variant="ghost"
          />
          <UButton
            icon="i-lucide-pencil"
            color="neutral"
            :aria-label="t('pages.admin.syncJobs.actions.edit')"
            @click="openEditDialog(row.original)"
            variant="ghost"
          />
          <UButton
            icon="i-lucide-trash"
            color="error"
            :aria-label="t('pages.admin.syncJobs.actions.delete')"
            @click="confirmDelete(row.original)"
            variant="ghost"
          />
        </div>
      </template>
    </AppDataTableShell>

    <AppModal
      v-model:open="dialogVisible"
      :title="
        selectedJob
          ? t('pages.admin.syncJobs.dialog.editTitle')
          : t('pages.admin.syncJobs.dialog.createTitle')
      "
      modal
      :style="{ width: '32rem' }"
    >
      <div class="flex flex-col gap-4">
        <div>
          <label class="mb-2 block text-sm font-medium">
            {{ t('pages.admin.syncJobs.fields.name') }}
          </label>
          <UInput
            v-model="formData.name"
            class="w-full"
            :class="{ 'border-error ring-1 ring-error': formErrors.name }"
          />
          <small v-if="formErrors.name" class="block text-red-700 dark:text-red-400">{{
            formErrors.name
          }}</small>
        </div>

        <div>
          <label class="mb-2 block text-sm font-medium">
            {{ t('pages.admin.syncJobs.fields.language') }}
          </label>
          <AppSelect
            v-model="formData.language"
            :options="languageOptions"
            option-label="label"
            option-value="value"
            class="w-full"
            :class="{ 'border-error ring-1 ring-error': formErrors.language }"
          />
          <small v-if="formErrors.language" class="block text-red-700 dark:text-red-400">{{
            formErrors.language
          }}</small>
        </div>

        <div>
          <label class="mb-2 block text-sm font-medium">
            {{ t('pages.admin.syncJobs.fields.iliasCourseId') }}
          </label>
          <UInput
            v-model="formData.iliasCourseId"
            class="w-full"
            :class="{ 'border-error ring-1 ring-error': formErrors.iliasCourseId }"
          />
          <small v-if="formErrors.iliasCourseId" class="block text-red-700 dark:text-red-400">
            {{ formErrors.iliasCourseId }}
          </small>
        </div>

        <div>
          <label class="mb-2 block text-sm font-medium">
            {{ t('pages.admin.syncJobs.fields.semester') }}
          </label>
          <AppSelect
            v-model="formData.semesterId"
            :options="semesterOptions"
            option-label="label"
            option-value="value"
            class="w-full"
            :class="{ 'border-error ring-1 ring-error': formErrors.semesterId }"
          />
          <small v-if="formErrors.semesterId" class="block text-red-700 dark:text-red-400">
            {{ formErrors.semesterId }}
          </small>
        </div>

        <div class="flex items-center gap-3">
          <USwitch v-model="formData.isActive" />
          <label class="text-sm font-medium">{{ t('pages.admin.syncJobs.fields.isActive') }}</label>
        </div>
      </div>

      <template #footer>
        <UButton
          :label="t('common.actions.cancel')"
          @click="dialogVisible = false"
          variant="ghost"
        />
        <UButton :label="t('common.actions.save')" :loading="isSaving" @click="saveJob" />
      </template>
    </AppModal>
  </div>
</template>
